/**
 * Stereo To Spatial component for the home page.
 *
 * Author: Bounlith Sirivong <bounlith.sirivong@ircamamplify.com>
 * Copyright (c) Ircam Amplify 2025
 */

import Cookies from 'js-cookie'

import { downloader } from '@/config/downloader'
import { logger } from '@/config/logger'
import { LogTag } from '@amplify3/duplo-back-end'
import { fetchAuthSession } from 'aws-amplify/auth'
import { useCallback, useEffect, useMemo, useState } from 'react'
import '../../../components/jobui/styles/globals.css'
import '../../../components/jobui/styles/input.css'
import ModuleConfigList from '../../ModuleConfig/ModuleConfigList'
import { ModuleKey } from '../../ModuleConfig/ModuleInfo'
import HomeModule from '../HomeModule'
import * as Def from '../HomeModuleDef'
import { ProcessingResult } from '../ProcessingResult'
import HomeStereoToSpatialResult from './HomeStereoToSpatialResult'
import StereoToSpatialParamComp from './StereoToSpatialParamComp'
import {
  StereoToSpatialCookie,
  StereoToSpatialResult,
} from './StereoToSpatialResult'
import './styles/stereo-to-spatial.css'

interface HomeStereoToSpatialProps {
  id?: string
}

const HomeStereoToSpatial = (props: HomeStereoToSpatialProps) => {
  const [processingResult, setProcessingResult] = useState<
    StereoToSpatialResult | undefined
  >(undefined)

  const [moduleParamList, setModuleParamList] = useState<Record<string, any>>(
    {}
  )

  useEffect(() => {
    logger.debug(LogTag.HomePage, 'moduleParamList', moduleParamList)
  }, [moduleParamList])

  // Use useMemo to avoid creating a new reference every render
  const stableModuleParamList = useMemo(
    () => moduleParamList,
    [moduleParamList]
  )

  // Use useCallback to prevent unnecessary re-renders
  const updateModuleParamList = useCallback((newParam: Record<string, any>) => {
    setModuleParamList((prev) => ({
      ...prev,
      ...newParam,
    }))
  }, [])

  function createProcessingResult() {
    return new StereoToSpatialResult()
  }

  function handleProcessingResult(
    response: any,
    jobId: string,
    fileName?: string
  ) {
    const backendResult = response.data.job_infos.report_info!.report

    const processingResult = new StereoToSpatialResult({
      fileName: fileName,
      jobId: jobId,
      processingIsoDate: response.data.job_infos.job_start_datetime,
      userId: Cookies.get(Def.COOKIE_USER_ID),
      iasSpatializedId: backendResult.archiveFile.id,
    })

    setProcessingResult(processingResult)
    processingResult.save()
  }

  function cleanSavedData() {
    StereoToSpatialResult.cleanSavedData()
  }

  function getSavedIasUrl() {
    return Cookies.get(StereoToSpatialCookie.IasUrl)
  }

  function getSavedFileName() {
    return Cookies.get(StereoToSpatialCookie.FileName)
  }

  async function handleDownloadResult(result: ProcessingResult) {
    logger.debug(LogTag.HomePage, 'handleDownloadResult...')
    const stereoToSpatialResult = result as StereoToSpatialResult
    if (stereoToSpatialResult.iasSpatializedId) {
      const session = await fetchAuthSession()
      await downloader.asyncDownloadFromIas(
        session,
        stereoToSpatialResult.iasSpatializedId
      )
    }
    logger.debug(LogTag.HomePage, '... handleDownloadResult done.')
  }

  return (
    <HomeModule
      id={props.id}
      title="Stereo to Spatial"
      description="Spatialize stereo files."
      iconSrc="/icon/stereo-to-spatial.svg"
      iconAlt="Stereo to Spatial"
      costText="1 credit/min. processed"
      moduleConfig={ModuleConfigList[ModuleKey.StereoToSpatial]}
      moduleParamComp={StereoToSpatialParamComp}
      updateModuleParamList={updateModuleParamList}
      moduleParamList={stableModuleParamList}
      handleProcessingResult={handleProcessingResult}
      createProcessingResult={createProcessingResult}
      setProcessingResult={setProcessingResult}
      processingResult={processingResult}
      resultComponent={HomeStereoToSpatialResult}
      cleanSavedData={cleanSavedData}
      getSavedIasUrl={getSavedIasUrl}
      getSavedFileName={getSavedFileName}
      handleDownloadResult={handleDownloadResult}
      restartButtonMsg="Spatialize another track"
    />
  )
}

export default HomeStereoToSpatial
